import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Filter, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { FilterValues } from "../hooks/useCompanyFilters";

export type { FilterValues };

interface CompaniesFilterDialogProps {
  filters: FilterValues;
  onFiltersChange: (filters: FilterValues) => void;
  onClearFilters: () => void;
}

export function CompaniesFilterDialog({
  filters,
  onFiltersChange,
  onClearFilters,
}: CompaniesFilterDialogProps) {
  const [open, setOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<FilterValues>(filters);

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    setOpen(false);
  };

  const handleClearFilters = () => {
    onClearFilters();
    setLocalFilters({
      page: 1,
      limit: 10,
      sortBy: "createdAt",
      sortOrder: "desc",
    });
    setOpen(false);
  };

  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => 
      key !== "page" && 
      key !== "limit" && 
      key !== "sortBy" && 
      key !== "sortOrder" && 
      value !== undefined && 
      value !== null && 
      value !== ""
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="ml-2">
          <Filter className="mr-2 h-4 w-4" />
          Filters
          {hasActiveFilters && (
            <span className="ml-2 h-2 w-2 rounded-full bg-blue-600" />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Filter Companies</DialogTitle>
          <DialogDescription>
            Apply filters to narrow down the companies list.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              Name
            </Label>
            <Input
              id="name"
              value={localFilters.name || ""}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, name: e.target.value })
              }
              className="col-span-3"
              placeholder="Search by company name..."
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="companyAdmin" className="text-right">
              Admin
            </Label>
            <Input
              id="companyAdmin"
              value={localFilters.companyAdmin || ""}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, companyAdmin: e.target.value })
              }
              className="col-span-3"
              placeholder="Search by admin name..."
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">Created From</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "col-span-3 justify-start text-left font-normal",
                    !localFilters.startCreatedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {localFilters.startCreatedDate ? (
                    format(new Date(localFilters.startCreatedDate), "PPP")
                  ) : (
                    <span>Pick a date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={localFilters.startCreatedDate ? new Date(localFilters.startCreatedDate) : undefined}
                  onSelect={(date) =>
                    setLocalFilters({
                      ...localFilters,
                      startCreatedDate: date ? format(date, "yyyy-MM-dd") : undefined,
                    })
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">Created To</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "col-span-3 justify-start text-left font-normal",
                    !localFilters.endCreatedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {localFilters.endCreatedDate ? (
                    format(new Date(localFilters.endCreatedDate), "PPP")
                  ) : (
                    <span>Pick a date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={localFilters.endCreatedDate ? new Date(localFilters.endCreatedDate) : undefined}
                  onSelect={(date) =>
                    setLocalFilters({
                      ...localFilters,
                      endCreatedDate: date ? format(date, "yyyy-MM-dd") : undefined,
                    })
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {hasActiveFilters && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {filters.name && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs">
                  Name: {filters.name}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setLocalFilters({ ...localFilters, name: undefined })}
                  />
                </span>
              )}
              {filters.companyAdmin && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs">
                  Admin: {filters.companyAdmin}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setLocalFilters({ ...localFilters, companyAdmin: undefined })}
                  />
                </span>
              )}
              {filters.startCreatedDate && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs">
                  From: {format(new Date(filters.startCreatedDate), "MMM dd")}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setLocalFilters({ ...localFilters, startCreatedDate: undefined })}
                  />
                </span>
              )}
              {filters.endCreatedDate && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs">
                  To: {format(new Date(filters.endCreatedDate), "MMM dd")}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setLocalFilters({ ...localFilters, endCreatedDate: undefined })}
                  />
                </span>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClearFilters}>
            Clear All
          </Button>
          <Button onClick={handleApplyFilters}>Apply Filters</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
